import * as Square from "@square/web-sdk";
import { useEffect, useMemo, useState } from "react";

import {
  computeSquareFormValidity,
  SquareFormFieldValidity,
} from "../computeSquareFormValidity";

// Square card events we subscribe to in order to keep per-field validity up
// to date. Together these cover the cases `isCompletelyValid` can transition:
//
//   - `cardBrandChanged` / `postalCodeChanged` fire on every keystroke in
//     their respective fields, giving us a near-realtime signal.
//   - `errorClassAdded` / `errorClassRemoved` fire whenever Square toggles
//     its `is-error` CSS class on a field.
//   - `focusClassAdded` / `focusClassRemoved` re-report the current validity
//     of the affected field on focus/blur.
const LISTENED_EVENTS: Square.CardInputEventTypesValues[] = [
  "cardBrandChanged",
  "postalCodeChanged",
  "errorClassAdded",
  "errorClassRemoved",
  "focusClassAdded",
  "focusClassRemoved",
];

/**
 * Tracks per-field validity reported by a Square `Card` element and returns
 * the derived "is the form valid?" boolean. Attaches event listeners when a
 * card becomes available and cleans them up on unmount or when the card
 * reference changes.
 *
 * Decision logic lives in `computeSquareFormValidity` and is tested
 * independently.
 */
export const useSquareFormValidity = (
  card: Square.Card | undefined
): boolean => {
  const [fieldValidity, setFieldValidity] = useState<SquareFormFieldValidity>(
    {}
  );

  useEffect(() => {
    if (!card) return;

    const handleCardInputEvent = (
      event: Square.SqEvent<Square.CardInputEvent>
    ) => {
      setFieldValidity((prev) => ({
        ...prev,
        [event.detail.field]: event.detail.currentState.isCompletelyValid,
      }));
    };

    LISTENED_EVENTS.forEach((eventType) => {
      card.addEventListener(eventType, handleCardInputEvent);
    });

    return () => {
      LISTENED_EVENTS.forEach((eventType) => {
        card.removeEventListener(eventType, handleCardInputEvent);
      });
    };
  }, [card]);

  return useMemo(
    () => computeSquareFormValidity(fieldValidity),
    [fieldValidity]
  );
};

// Re-export for tests that want to verify exact event coverage.
export const SQUARE_FORM_LISTENED_EVENTS = LISTENED_EVENTS;
